import {
  mailConnectionProbeSchema,
  mailConnectionSchema,
  mailConnectionTestResponseSchema,
  type MailConnectionProbe,
  type MailConnectionTestResponse,
  type UpdateMailConnectionRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';

/** Matches `DataInvalidationScope.MAIL` so a save refreshes this screen without a reload. */
export const mailConnectionQueryKey = ['mail', 'connection'] as const;

const queuedSchema = z.object({ queued: z.literal(true) });

export function useMailConnection(enabled = true) {
  return useQuery({
    queryKey: mailConnectionQueryKey,
    queryFn: () => apiFetch('/mail/connection', mailConnectionSchema),
    refetchOnWindowFocus: true,
    enabled,
  });
}

export function useUpdateMailConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMailConnectionRequest) =>
      apiFetch('/mail/connection', mailConnectionSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(mailConnectionQueryKey, data);
    },
  });
}

export function useProbeMailConnection() {
  return useMutation({
    mutationFn: (): Promise<MailConnectionProbe> =>
      apiFetch('/mail/connection/probe', mailConnectionProbeSchema, { method: 'POST' }),
  });
}

export function useTestMailConnection() {
  return useMutation({
    mutationFn: (): Promise<MailConnectionTestResponse> =>
      apiFetch('/mail/connection/test', mailConnectionTestResponseSchema, { method: 'POST' }),
  });
}

/** Same endpoint as Helpdesk „Jetzt abrufen“ — enqueues `mail_poll`, no second client. */
export function usePollMailNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/integrations/mail/poll', queuedSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: mailConnectionQueryKey });
    },
  });
}
